/**
 * Prototype Augments + Skill Tier non-linear scaling — loaded from
 * /public/data/prototype-augments.json + /public/data/prototype-skill-tier.json.
 *
 * Lightweight on-demand loader; data fetched once and cached.
 */

export interface PrototypeAugmentBonus {
  stat: string;
  value: number;
}

export interface PrototypeAugment {
  id: string;
  name_en: string;
  name_ru: string;
  description_en: string;
  description_ru: string;
  bonuses: PrototypeAugmentBonus[];
  slot_requirement: string | null;
}

interface PrototypeAugmentsFile {
  version: string;
  gameVersion: string;
  augments: PrototypeAugment[];
}

interface PrototypeSkillTierFile {
  version: string;
  gameVersion: string;
  /** "0".."4" → bonus tiers (non-linear). */
  skill_tier_bonus_table: Record<string, number>;
  per_item_delta: Record<string, number>;
  max_items_gear: number;
}

export interface PrototypeData {
  augments: PrototypeAugment[];
  byId: Map<string, PrototypeAugment>;
  skillTierTable: Record<number, number>;
  maxItems: number;
}

let cache: PrototypeData | null = null;
let loading: Promise<PrototypeData> | null = null;

declare const __BUILD_TS__: string | undefined;
const BUILD_TOKEN = typeof __BUILD_TS__ !== 'undefined' ? __BUILD_TS__ : String(Date.now());

async function fetchJson<T>(url: string): Promise<T> {
  const sep = url.includes('?') ? '&' : '?';
  const r = await fetch(`${url}${sep}v=${BUILD_TOKEN}`, { cache: 'no-cache' });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return (await r.json()) as T;
}

export async function loadPrototypeData(): Promise<PrototypeData> {
  if (cache) return cache;
  if (loading) return loading;
  loading = (async () => {
    const [augFile, tierFile] = await Promise.all([
      fetchJson<PrototypeAugmentsFile>('/data/prototype-augments.json'),
      fetchJson<PrototypeSkillTierFile>('/data/prototype-skill-tier.json'),
    ]);
    const skillTierTable: Record<number, number> = {};
    for (const [k, v] of Object.entries(tierFile.skill_tier_bonus_table)) {
      const n = Number.parseInt(k, 10);
      if (Number.isFinite(n)) skillTierTable[n] = v;
    }
    const data: PrototypeData = {
      augments: augFile.augments,
      byId: new Map(augFile.augments.map((a) => [a.id, a])),
      skillTierTable,
      maxItems: tierFile.max_items_gear ?? 4,
    };
    cache = data;
    return data;
  })();
  return loading;
}

/** Synchronous accessor — returns cached data or null if not yet loaded. */
export function getPrototypeData(): PrototypeData | null {
  return cache;
}

/**
 * Apply prototype non-linear skill_tier scaling.
 * Input: number of gear slots with isPrototype=true and coreStat='skill_tier' (0..4).
 * Returns the bonus tiers from the table {0:0, 1:1, 2:3, 3:4, 4:6}.
 * Pre-TU22.1 was linear (1=1, 2=2, 3=3, 4=4); now non-linear.
 */
export function prototypeSkillTierBonus(prototypeSkillTierCount: number): number {
  if (!cache) return 0;
  const clamped = Math.max(0, Math.min(cache.maxItems, Math.floor(prototypeSkillTierCount)));
  return cache.skillTierTable[clamped] ?? 0;
}
